import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Building2 } from "lucide-react";
import { useMe } from "@/lib/me";
import ClubAutocomplete from "./club-autocomplete";

interface Props {
  children: (clubId: string) => React.ReactNode;
}

export default function ClubScopeShell({ children }: Props) {
  const { t } = useTranslation();
  const { user, club, isLoading } = useMe();
  const [picked, setPicked] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  if (user.role === "MANAGER" || user.role === "STAFF") {
    if (!club) {
      return (
        <div className="rounded-xl border border-dashed border-neutral-200 px-6 py-10 text-center">
          <p className="text-sm text-neutral-500">
            {t("autocomplete.managerNoClub")}
          </p>
        </div>
      );
    }
    return <>{children(club.id)}</>;
  }

  if (user.role === "SUPERADMIN") {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-neutral-200 p-4 space-y-2">
          <label className="text-sm font-medium text-neutral-700">
            {t("autocomplete.pickClubLabel")}
          </label>
          <ClubAutocomplete value={picked} onChange={setPicked} />
        </div>
        {picked ? (
          children(picked)
        ) : (
          <div className="rounded-xl border border-dashed border-neutral-200 px-6 py-10 text-center">
            <Building2 className="w-6 h-6 text-neutral-300 mx-auto mb-2" />
            <p className="text-sm text-neutral-500">
              {t("autocomplete.selectAClubFirst")}
            </p>
          </div>
        )}
      </div>
    );
  }

  return <Navigate to="/" replace />;
}
